import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import PageHeaderContainer from 'containers/PageHeader';
import {
  STORES_TYPE,
  ITEMS_TYPE,
  ACCOUNTS_TYPE,
  TAB_TO_QUERY_PARAM,
  GlobalFilterKey
} from 'utils/types';
import { periodEndWeekSelector } from 'containers/AccessControl/selectors';
import * as globalSelectors from 'store/global/selectors';
import SalesHighlightsWidget from 'components/SalesHighlightsWidget';
import * as pageHeaderActions from 'containers/PageHeader/actions';
import { detailsFilterSelector } from 'containers/PageHeader/selectors';
import { OTHER_ROUTES } from 'utils/routes';
import FilterBarContainer from 'containers/PageHeader/FilterBar';
import { PreloadedType } from 'containers/PageHeader/FilterModal';
import ReactGA from 'react-ga';
import usePrevious from 'hooks/use-previous';
import {
  QueryResult,
  QueryStateResult,
  useTableQuery,
  useTableQueryState
} from '@spins/react-table';
import { ACCOUNTS_DETAIL_SERIES, ITEMS_DETAIL_SERIES, STORES_DETAIL_SERIES } from 'apollo/queries';
import StoresTable from 'components/Tables/Stores';
import ItemsTable from 'components/Tables/Items';
import AccountsTable from 'components/Tables/Accounts';
import TableTabs from 'components/TableTabs';
import { SearchInput, Widget } from '@spins/react-ui-library';
import DistributionChartWidget from 'components/Charts/Widgets/DistributionChartWidget';
import TrendChartWidget from 'components/Charts/Widgets/TrendChartWidget';
import TableFilterTiles from 'components/Filter/TableFilterTiles';

const Details = () => {
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const dispatch = useDispatch();
  const clientId: string = useSelector(globalSelectors.clientIdSelector);
  const currentBrand: string = useSelector(globalSelectors.currentBrandSelector);
  const globalFilter = useSelector(detailsFilterSelector);
  const periodEndWeek = useSelector(periodEndWeekSelector);
  const isBrandFilterable = useSelector(globalSelectors.isBrandFilterableSelector);
  const [selectedTab, setSelectedTab] = useState('');
  const [showAccounts, setShowAccounts] = useState(false);
  const [showStores, setShowStores] = useState(false);
  const [showItems, setShowItems] = useState(false);

  const previousLocationSearch: string = usePrevious<string>(location.search);

  useEffect(() => {
    ReactGA.pageview(OTHER_ROUTES.DETAILS);
  }, []);

  // tables state
  const tablesState: QueryStateResult = useTableQueryState([
    {
      id: 'accounts',
      defaultSortAttribute: 'sales',
      queryVariables: {
        clientID: clientId,
        globalFilter: globalFilter
      }
    },
    {
      id: 'stores',
      defaultSortAttribute: 'sales',
      queryVariables: {
        clientID: clientId,
        globalFilter: globalFilter
      }
    },
    {
      id: 'items',
      defaultSortAttribute: 'sales',
      queryVariables: {
        clientID: clientId,
        globalFilter: globalFilter
      }
    }
  ]);

  // accounts query
  const {
    data: accountsData,
    loading: accountsLoading,
    error: accountsError
  }: QueryResult = useTableQuery(ACCOUNTS_DETAIL_SERIES, {
    fetchPolicy: 'cache-first',
    queryState: tablesState.queryState('accounts'),
    searchAttributes: ['name'],
    skip: params.has('account')
  });

  // stores query
  const {
    data: storesData,
    loading: storesLoading,
    error: storesError
  }: QueryResult = useTableQuery(STORES_DETAIL_SERIES, {
    fetchPolicy: 'cache-first',
    queryState: tablesState.queryState('stores'),
    searchAttributes: ['name'],
    skip: params.has('store')
  });

  // items query
  const { data: itemsData, loading: itemsLoading, error: itemsError }: QueryResult = useTableQuery(
    ITEMS_DETAIL_SERIES,
    {
      fetchPolicy: 'cache-first',
      queryState: tablesState.queryState('items'),
      searchAttributes: ['name'],
      skip: params.has('item')
    }
  );

  /**
   * Here we set which tables (and tabs) should be visible, and also the default one selected.
   */
  const setTabs = useCallback(() => {
    const params = new URLSearchParams(location.search);
    setShowAccounts(false);
    setShowStores(false);
    setShowItems(false);
    if (params.has('account')) {
      if (params.has('item')) {
        setSelectedTab(STORES_TYPE);
        setShowStores(true);
      } else if (params.has('store')) {
        setSelectedTab(ITEMS_TYPE);
        setShowItems(true);
      } else {
        setSelectedTab(STORES_TYPE);
        setShowStores(true);
        setShowItems(true);
      }
    } else if (params.has('item')) {
      if (params.has('store')) {
        setSelectedTab(ACCOUNTS_TYPE);
        setShowAccounts(true);
      } else {
        setSelectedTab(ACCOUNTS_TYPE);
        setShowAccounts(true);
        setShowStores(true);
      }
    } else if (params.has('store')) {
      setSelectedTab(ITEMS_TYPE);
      setShowItems(true);
    }
  }, [location.search]);

  const updateGlobalFilterFromParams = useCallback(() => {
    const params = new URLSearchParams(location.search);
    dispatch(pageHeaderActions.clearGlobalFilter(GlobalFilterKey.DETAILS));
    if (params.has('account')) {
      dispatch(
        pageHeaderActions.updateGlobalFilterArray(
          [decodeURIComponent(params.get('account'))],
          'accounts',
          GlobalFilterKey.DETAILS
        )
      );
    }
    if (params.has('store')) {
      dispatch(
        pageHeaderActions.updateGlobalFilterArray(
          [params.get('store')],
          'stores',
          GlobalFilterKey.DETAILS
        )
      );
    }
    if (params.has('item')) {
      dispatch(
        pageHeaderActions.updateGlobalFilterArray(
          [params.get('item')],
          'items',
          GlobalFilterKey.DETAILS
        )
      );
    }
  }, [dispatch, location.search]);

  /**
   * In case the location state have some info (set for example when coming from favorites), the
   * global filter should be updated with that info.
   */
  const updateFromLocationState = useCallback(() => {
    if (location.state) {
      // @ts-ignore
      if (location.state.weeks) {
        // @ts-ignore
        dispatch(pageHeaderActions.updateGlobalFilterWeeks(location.state.weeks));
      }
      // @ts-ignore
      if (location.state.states) {
        dispatch(
          pageHeaderActions.updateGlobalFilterArray(
            // @ts-ignore
            location.state.states,
            'states',
            GlobalFilterKey.DETAILS
          )
        );
      }
      // @ts-ignore
      if (location.state.prevPath === 'favorites') {
        // @ts-ignore
        if (location.state.items) {
          dispatch(
            pageHeaderActions.updateGlobalFilterArray(
              // @ts-ignore
              location.state.items,
              'items',
              GlobalFilterKey.DETAILS
            )
          );
        }
        // @ts-ignore
        if (location.state.accounts) {
          dispatch(
            pageHeaderActions.updateGlobalFilterArray(
              // @ts-ignore
              location.state.accounts,
              'accounts',
              GlobalFilterKey.DETAILS
            )
          );
        }
        // @ts-ignore
        if (location.state.stores) {
          dispatch(
            pageHeaderActions.updateGlobalFilterArray(
              // @ts-ignore
              location.state.stores,
              'stores',
              GlobalFilterKey.DETAILS
            )
          );
        }
      }
    }
  }, [dispatch, location.state]);

  const updateGlobalFilterAndTabs = useCallback(() => {
    updateGlobalFilterFromParams();
    setTabs();
  }, [setTabs, updateGlobalFilterFromParams]);

  useEffect(() => {
    if (previousLocationSearch !== location.search) {
      updateGlobalFilterAndTabs();
    }
  }, [dispatch, location.search, previousLocationSearch, updateGlobalFilterAndTabs]);

  useEffect(() => {
    updateGlobalFilterAndTabs();
    updateFromLocationState();
    return () => {
      dispatch(pageHeaderActions.clearGlobalFilter(GlobalFilterKey.DETAILS));
    };
  }, [dispatch, updateFromLocationState, updateGlobalFilterAndTabs]);

  const typesPreloaded: PreloadedType = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      accounts: params.has('account'),
      items: params.has('item'),
      states: params.has('state'),
      stores: params.has('store')
    };
  }, [location.search]);

  const handleBreadcrumbChange = selectedTab => {
    const params = new URLSearchParams(location.search);
    // Update params with breadcrumb changes, the details update will be done on componentDidUpdate
    let selectedTabProcessed = false;
    let keysToDelete = [];
    params.forEach((_value, key) => {
      if (selectedTabProcessed) {
        keysToDelete.push(key);
      }
      if (key === TAB_TO_QUERY_PARAM.get(selectedTab)) {
        selectedTabProcessed = true;
      }
    });
    keysToDelete.forEach(val => params.delete(val));
  };

  const getTemplate = () => {
    const params = new URLSearchParams(location.search);
    switch (selectedTab) {
      case ACCOUNTS_TYPE:
        return params.has('item') ? 'itemDetails' : '';
      case STORES_TYPE:
        return 'accountOverview';
      case ITEMS_TYPE:
        return 'itemDetails';
      default:
        return '';
    }
  };

  const handleDrillDown = (key: string, row: any) => {
    tablesState.sharedState.filtering.removeAll();
    let params = new URLSearchParams(location.search);
    let entity = TAB_TO_QUERY_PARAM.get(selectedTab);
    let name = '';
    if (params.has('account') && params.has('store') && params.has('item')) {
      return false;
    }
    if (selectedTab === STORES_TYPE) {
      let storeInfo = row.name.split(',');
      name = storeInfo[0] + ', ' + storeInfo[1];
    } else {
      name = row.name;
    }
    params.delete(entity);
    params.append(entity, encodeURIComponent(key));
    params.delete(`${entity}_name`);
    params.append(`${entity}_name`, encodeURIComponent(name));
    history.push({
      pathname: OTHER_ROUTES.DETAILS,
      search: `?${params.toString()}`,
      state: row
    });
  };

  const hideTable = ['accounts', 'stores', 'items'].every(key => globalFilter[key].length > 0);
  const showTopOpportunity = params.has('account') && !params.has('item');
  return (
    <React.Fragment>
      <Row>
        <PageHeaderContainer
          globalFilterContext={GlobalFilterKey.DETAILS}
          typesPreloaded={typesPreloaded}
          onSelectedTabChange={handleBreadcrumbChange}
          template={getTemplate()}
          exportPdfAvailable={true}
        />
      </Row>
      <FilterBarContainer
        globalFilterContext={GlobalFilterKey.DETAILS}
        typesPreloaded={typesPreloaded}
      />
      <Row className="mt-3">
        <Col>
          <SalesHighlightsWidget
            clientId={clientId}
            periodEndWeek={periodEndWeek}
            currentBrand={currentBrand}
            globalFilter={globalFilter}
          />
        </Col>
      </Row>
      <Row>
        <Col className="mt-3" lg={12} xl={6}>
          <TrendChartWidget globalFilter={globalFilter} />
        </Col>
        <Col className="mt-3" lg={12} xl={6}>
          <DistributionChartWidget globalFilter={globalFilter} />
        </Col>
      </Row>
      {tablesState.sharedState.filtering.filters.length > 0 && (
        <Row className="mt-3">
          <Col>
            <TableFilterTiles filtering={tablesState.sharedState.filtering} />
          </Col>
        </Row>
      )}
      {!hideTable && (
        <Row className="mt-3">
          <Col>
            <Widget>
              {{
                header: {
                  content: {
                    left: (
                      <TableTabs
                        selectedTab={selectedTab}
                        tabs={[
                          {
                            name: ACCOUNTS_TYPE,
                            total:
                              accountsData && accountsData.accountDetailsSeries
                                ? accountsData.accountDetailsSeries.page.totalItems
                                : 0
                          },
                          {
                            name: STORES_TYPE,
                            total:
                              storesData && storesData.storeDetailsSeries
                                ? storesData.storeDetailsSeries.page.totalItems
                                : 0
                          },
                          {
                            name: ITEMS_TYPE,
                            total:
                              itemsData && itemsData.itemsDetailsSeries
                                ? itemsData.itemsDetailsSeries.page.totalItems
                                : 0
                          }
                        ].filter(tab => {
                          // Hide tabs that should not be shown
                          if (tab.name === ACCOUNTS_TYPE) {
                            return showAccounts;
                          }
                          if (tab.name === STORES_TYPE) {
                            return showStores;
                          }
                          if (tab.name === ITEMS_TYPE) {
                            return showItems;
                          }
                          return true;
                        })}
                        onSelect={key => {
                          setSelectedTab(key);
                        }}
                      />
                    ),
                    center: (
                      <SearchInput
                        style={{ width: '300px' }}
                        placeholder="Search..."
                        onChange={value => {
                          tablesState.sharedState.searching.search(value);
                        }}
                      />
                    )
                  }
                },
                body: {
                  content: (
                    <>
                      {selectedTab === ACCOUNTS_TYPE && showAccounts && (
                        <AccountsTable
                          error={accountsError}
                          queryState={tablesState.queryState('accounts')}
                          rows={
                            accountsData && accountsData.accountDetailsSeries
                              ? accountsData.accountDetailsSeries.data
                              : []
                          }
                          showLoading={accountsLoading}
                          totalRows={
                            accountsData && accountsData.accountDetailsSeries
                              ? accountsData.accountDetailsSeries.page.totalItems
                              : 0
                          }
                          onRowClick={isBrandFilterable && (row => handleDrillDown(row.name, row))}
                        />
                      )}
                      {selectedTab === STORES_TYPE && showStores && (
                        <StoresTable
                          error={storesError}
                          queryState={tablesState.queryState('stores')}
                          rows={
                            storesData && storesData.storeDetailsSeries
                              ? storesData.storeDetailsSeries.data
                              : []
                          }
                          showLoading={storesLoading}
                          showTopOpportunity={showTopOpportunity}
                          totalRows={
                            storesData && storesData.storeDetailsSeries
                              ? storesData.storeDetailsSeries.page.totalItems
                              : 0
                          }
                          onRowClick={
                            isBrandFilterable && (row => handleDrillDown(row.storeTag, row))
                          }
                        />
                      )}
                      {selectedTab === ITEMS_TYPE && showItems && (
                        <ItemsTable
                          error={itemsError}
                          queryState={tablesState.queryState('items')}
                          rows={
                            itemsData && itemsData.itemsDetailsSeries
                              ? itemsData.itemsDetailsSeries.data
                              : []
                          }
                          showLoading={itemsLoading}
                          totalRows={
                            itemsData && itemsData.itemsDetailsSeries
                              ? itemsData.itemsDetailsSeries.page.totalItems
                              : 0
                          }
                          onRowClick={isBrandFilterable && (row => handleDrillDown(row.upc, row))}
                        />
                      )}
                    </>
                  )
                }
              }}
            </Widget>
          </Col>
        </Row>
      )}
    </React.Fragment>
  );
};

export default Details;
